import React, { ReactNode, useState, useEffect, Suspense, createContext, useContext } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { CLUB_ADMIN_FEATURES } from '@/config/features';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// Context to prevent double-rendering of layout when pages are nested
const ClubAdminLayoutContext = createContext(false);

/**
 * Hook to check if we're inside a ClubAdminLayout
 * Used by pages to skip their own layout wrapper when nested
 */
export const useIsInsideClubAdminLayout = () => useContext(ClubAdminLayoutContext);

// Context for the week selector (used in Reports pages)
export interface WeekSelectorContextType {
  selectedWeek: string; // '0' = This Week, '1' = Last Week, etc.
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

const WeekSelectorContext = createContext<WeekSelectorContextType | null>(null);

/**
 * Hook to get the selected week and date range from the layout
 * Used by Reports components to filter data by the selected week
 */
export const useWeekSelector = () => {
  const context = useContext(WeekSelectorContext);
  if (!context) {
    // Return default "This Week" if not inside layout
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() + diffToMonday);
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    return {
      selectedWeek: '0',
      dateRange: { startDate: weekStart, endDate: weekEnd }
    };
  }
  return context;
};

// Context for passing custom header actions from nested pages to parent layout
interface CustomHeaderActionsContextType {
  setCustomHeaderActions: (actions: ReactNode) => void;
}
const CustomHeaderActionsContext = createContext<CustomHeaderActionsContextType | null>(null);

/**
 * Hook for nested pages to set custom header actions in the parent layout
 * Use this when your page is rendered via Outlet inside ClubAdminLayout
 */
export const useSetCustomHeaderActions = () => {
  const context = useContext(CustomHeaderActionsContext);
  if (!context) {
    // Not inside a layout, return no-op
    return () => {};
  }
  return context.setCustomHeaderActions;
};
import {
  LayoutDashboard,
  Users,
  MapPin,
  MessageSquare,
  MessageCircle,
  Megaphone,
  BarChart3,
  Settings,
  Calendar,
  LogOut,
  ChevronDown,
  ChevronUp,
  Building2,
  Car,
  Trophy,
  Menu,
  X,
  UserCheck,
  Plus,
  Clock,
  LayoutGrid,
  CalendarPlus,
  TrendingUp,
  PanelLeftClose,
  PanelRightClose,
  GraduationCap,
  DollarSign,
  ShoppingBag,
  Receipt,
  Target,
  Lock,
  CreditCard,
  Globe,
  Inbox,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import { useClubAdmin } from '@/hooks/useClubAdmin';
import { MemberDetailPanelProvider } from '@/components/crm/MemberDetailPanelProvider';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Language } from '@/i18n/types';
import { useClubFeatureAccess } from '@/hooks/useClubFeatureAccess';
import { useStaffPermissions } from '@/hooks/useStaffPermissions';
import { useIsMobile } from '@/hooks/use-mobile';
import { PATH_TO_FEATURE_KEY } from '@/types/feature-access';
import { Breadcrumbs } from '@/components/admin/Breadcrumbs';
import { CrmPageTracker } from '@/components/analytics/CrmPageTracker';

// Content loading skeleton for route transitions
const ContentSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-8 w-16" />
        </div>
      ))}
    </div>
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <Skeleton className="h-6 w-32 mb-4" />
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-12 w-full rounded-lg" />
        ))}
      </div>
    </div>
  </div>
);

interface ClubAdminLayoutProps {
  children?: ReactNode; // Optional - if not provided, renders Outlet for nested routes
  onAddCourse?: () => void;
  onAddCart?: () => void;
  onNewCartBooking?: () => void;
  onExportCarts?: () => void;
  onCreateChannel?: () => void;
  onCreateAnnouncement?: () => void;
  onInviteMember?: () => void;
  onExportMembers?: () => void;
  onBulkImport?: () => void;
  onCreateTask?: () => void;
  onScheduleShift?: () => void;
  onDateRangeChange?: (value: string) => void;
  onFilters?: () => void;
  onSaveSettings?: () => void;
  isSaving?: boolean;
  customHeaderActions?: ReactNode;
}

interface SidebarItem {
  path: string;
  icon: any;
  label: string;
  children?: SidebarItem[];
}

const getSidebarItems = (t: any): SidebarItem[] => {
  const items: SidebarItem[] = [];

  items.push({ path: '/club-admin/dashboard', icon: LayoutDashboard, label: t.clubAdmin.navigation.dashboard });

  items.push({
    path: '/club-admin/members',
    icon: Users,
    label: t.clubAdmin.navigation.members,
    children: [
      { path: '/club-admin/members', icon: Users, label: t.clubAdmin.navigation.members },
      { path: '/club-admin/members/memberships', icon: CreditCard, label: t.clubAdmin.navigation.memberships },
      { path: '/club-admin/member-progress', icon: TrendingUp, label: t.clubAdmin.navigation.memberProgress },
      { path: '/club-admin/reports/members', icon: BarChart3, label: t.clubAdmin.navigation.memberEngagement ?? 'Member Reports' },
    ]
  });

  if (CLUB_ADMIN_FEATURES.staffManagement) {
    items.push({
      path: '/club-admin/staff',
      icon: UserCheck,
      label: t.clubAdmin.navigation.staffManagement,
      children: [
        { path: '/club-admin/staff', icon: LayoutDashboard, label: t.clubAdmin.navigation.staffSchedule },
        { path: '/club-admin/staff/people', icon: Users, label: t.clubAdmin.navigation.people },
      ]
    });
  }

  if (CLUB_ADMIN_FEATURES.courses) {
    items.push({ path: '/club-admin/courses', icon: MapPin, label: t.clubAdmin.navigation.courses });
  }

  if (CLUB_ADMIN_FEATURES.teeSheet) {
    items.push({
      path: '/club-admin/tee-sheet',
      icon: Clock,
      label: t.clubAdmin.navigation.teeSheet,
      children: [
        { path: '/club-admin/tee-sheet', icon: LayoutGrid, label: t.clubAdmin.navigation.teeBookings },
        { path: '/club-admin/tee-sheet/check-in', icon: UserCheck, label: t.clubAdmin.navigation.checkIn },
        { path: '/club-admin/tee-sheet/generate', icon: Calendar, label: t.clubAdmin.navigation.calendar },
        { path: '/club-admin/tee-sheet/analytics', icon: BarChart3, label: t.clubAdmin.navigation.analytics },
        { path: '/club-admin/tee-sheet/settings', icon: Settings, label: t.clubAdmin.navigation.settings },
      ]
    });
  }

  if (CLUB_ADMIN_FEATURES.services) {
    items.push({
      path: '/club-admin/services',
      icon: ShoppingBag,
      label: t.clubAdmin.navigation.services,
      children: [
        { path: '/club-admin/services', icon: Target, label: t.clubAdmin.navigation.range },
        { path: '/club-admin/services/carts', icon: Car, label: t.clubAdmin.navigation.carts },
        { path: '/club-admin/services/lockers', icon: Lock, label: t.clubAdmin.navigation.lockers },
        { path: '/club-admin/services/config', icon: Settings, label: t.clubAdmin.configuration },
      ]
    });
  }

  if (CLUB_ADMIN_FEATURES.revenue) {
    items.push({
      path: '/club-admin/revenue',
      icon: DollarSign,
      label: t.clubAdmin.navigation.revenue,
      children: [
        { path: '/club-admin/revenue', icon: LayoutDashboard, label: t.clubAdmin.navigation.dashboard },
        { path: '/club-admin/revenue/transactions', icon: Receipt, label: t.clubAdmin.navigation.transactions },
        { path: '/club-admin/revenue/settings', icon: Settings, label: t.clubAdmin.navigation.paymentSettings },
      ]
    });
  }

  items.push({ path: '/club-admin/events', icon: Trophy, label: t.clubAdmin.navigation.events });

  if (CLUB_ADMIN_FEATURES.coaching) {
    items.push({
      path: '/club-admin/coaching',
      icon: GraduationCap,
      label: t.clubAdmin.navigation.coaching,
      children: [
        { path: '/club-admin/coaching', icon: LayoutDashboard, label: t.clubAdmin.navigation.dashboard },
        { path: '/club-admin/coaching/my-profile', icon: Users, label: t.clubAdmin.navigation.myProfile },
        { path: '/club-admin/coaching/revenue', icon: DollarSign, label: t.clubAdmin.navigation.revenue },
        { path: '/club-admin/coaching/settings', icon: Settings, label: t.clubAdmin.navigation.settings },
      ]
    });
  }

  items.push({ path: '/club-admin/communication', icon: Inbox, label: t.clubAdmin.navigation.communication });
  items.push({ path: '/club-admin/announcements', icon: Megaphone, label: t.clubAdmin.navigation.announcements });

  if (CLUB_ADMIN_FEATURES.feedback) {
    items.push({ path: '/club-admin/feedback', icon: MessageCircle, label: t.clubAdmin.navigation.feedback });
  }

  items.push({
    path: '/club-admin/reports',
    icon: BarChart3,
    label: t.clubAdmin.navigation.reports ?? 'Reports',
    children: [
      { path: '/club-admin/reports', icon: LayoutDashboard, label: t.clubAdmin.navigation.overview ?? 'Overview' },
      { path: '/club-admin/reports/members', icon: Users, label: t.clubAdmin.navigation.memberEngagement ?? 'Member Engagement' },
      { path: '/club-admin/reports/revenue', icon: DollarSign, label: t.clubAdmin.navigation.reportsRevenue ?? 'Revenue' },
      ...(CLUB_ADMIN_FEATURES.teeSheet ? [{ path: '/club-admin/reports/tee-sheet', icon: Clock, label: t.clubAdmin.navigation.teeSheet }] : []),
      { path: '/club-admin/reports/communications', icon: MessageSquare, label: t.clubAdmin.navigation.communications ?? 'Communications' },
      ...(CLUB_ADMIN_FEATURES.services ? [{ path: '/club-admin/reports/services', icon: ShoppingBag, label: t.clubAdmin.navigation.services }] : []),
      { path: '/club-admin/reports/events', icon: Trophy, label: t.clubAdmin.navigation.events ?? 'Events' },
    ]
  });

  if (CLUB_ADMIN_FEATURES.analytics) {
    items.push({ path: '/club-admin/analytics', icon: BarChart3, label: t.clubAdmin.navigation.analytics });
  }

  items.push({
    path: '/club-admin/settings',
    icon: Settings,
    label: t.clubAdmin.navigation.settings,
    children: [
      { path: '/club-admin/settings', icon: Settings, label: t.clubAdmin.navigation.clubProfile },
      { path: '/club-admin/settings/coaches', icon: GraduationCap, label: t.clubAdmin.navigation.coaches },
    ],
  });

  return items;
};

export function ClubAdminLayout({ children, onAddCourse, onAddCart, onNewCartBooking, onExportCarts, onCreateChannel, onCreateAnnouncement, onInviteMember, onExportMembers, onBulkImport, onCreateTask, onScheduleShift, onDateRangeChange, onFilters, onSaveSettings, isSaving, customHeaderActions }: ClubAdminLayoutProps) {
  // Check if we're already inside a ClubAdminLayout (nested route scenario)
  const isInsideLayout = useContext(ClubAdminLayoutContext);

  // State for header actions passed from nested pages via context
  const [nestedHeaderActions, setNestedHeaderActions] = useState<ReactNode>(null);

  // If we're already inside a layout, just render children directly (skip double layout)
  // BUT if customHeaderActions is provided, we need to pass it up to parent layout
  if (isInsideLayout && children) {
    return <>{children}</>;
  }

  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const { t } = useAppTranslations();
  const { language, setLanguage } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Language options for the selector
  const languageOptions: { code: Language; label: string; flag: string }[] = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  ];
  const [avatarError, setAvatarError] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load collapsed state from localStorage, default to false
    const saved = localStorage.getItem('clubAdminSidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const isMobile = useIsMobile();
  // On mobile, always show expanded sidebar (collapsed only applies to desktop)
  const isEffectivelyCollapsed = sidebarCollapsed && !isMobile;

  // Week selector state for Reports page
  const [selectedWeek, setSelectedWeek] = useState('0'); // 0 = This Week

  // Generate week options for the selector
  const getWeekOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 6; i++) {
      const weekStart = new Date(today);
      const dayOfWeek = weekStart.getDay();
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      weekStart.setDate(weekStart.getDate() + diffToMonday - (i * 7));
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);
      const formatDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      let label = i === 0 ? (t.clubAdmin?.layout?.thisWeek ?? 'This Week') : i === 1 ? (t.clubAdmin?.layout?.lastWeek ?? 'Last Week') : (t.clubAdmin?.layout?.weeksAgo ?? '{count} Weeks Ago').replace('{count}', String(i));
      options.push({
        value: i.toString(),
        label,
        dateRange: `${formatDate(weekStart)} - ${formatDate(weekEnd)}`,
        startDate: weekStart,
        endDate: weekEnd,
      });
    }
    return options;
  };
  const weekOptions = getWeekOptions();
  const currentWeekOption = weekOptions.find(w => w.value === selectedWeek);

  // Calculate the date range for the currently selected week
  const weekSelectorContextValue: WeekSelectorContextType = {
    selectedWeek,
    dateRange: {
      startDate: currentWeekOption?.startDate || new Date(),
      endDate: currentWeekOption?.endDate || new Date(),
    }
  };

  // Period selector state for Coaching Revenue page
  const [selectedCoachingPeriod, setSelectedCoachingPeriod] = useState('month');
  const coachingPeriodOptions = React.useMemo(() => [
    { value: 'week', label: t.clubAdmin?.layout?.thisWeek ?? 'This Week' },
    { value: 'month', label: t.clubAdmin?.layout?.thisMonth ?? 'This Month' },
    { value: 'year', label: t.clubAdmin?.layout?.thisYear ?? 'This Year' },
    { value: 'all', label: t.clubAdmin?.layout?.allTime ?? 'All Time' },
  ], [t]);
  const currentCoachingPeriod = coachingPeriodOptions.find(p => p.value === selectedCoachingPeriod);

  // Reset avatar error when profile changes
  useEffect(() => {
    setAvatarError(false);
  }, [profile?.avatar_url]);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    // Auto-expand section if current path is a child
    const expanded = new Set<string>();
    if (location.pathname.startsWith('/club-admin/tee-sheet')) {
      expanded.add('/club-admin/tee-sheet');
    }
    if (location.pathname.startsWith('/club-admin/member') || location.pathname === '/club-admin/reports/members') {
      expanded.add('/club-admin/members');
    }
    if (location.pathname.startsWith('/club-admin/staff')) {
      expanded.add('/club-admin/staff');
    }
    if (location.pathname.startsWith('/club-admin/coaching')) {
      expanded.add('/club-admin/coaching');
    }
    if (location.pathname.startsWith('/club-admin/services')) {
      expanded.add('/club-admin/services');
    }
    if (location.pathname.startsWith('/club-admin/revenue')) {
      expanded.add('/club-admin/revenue');
    }
    if (location.pathname.startsWith('/club-admin/reports') && location.pathname !== '/club-admin/reports/members') {
      expanded.add('/club-admin/reports');
    }
    if (location.pathname.startsWith('/club-admin/settings')) {
      expanded.add('/club-admin/settings');
    }
    return expanded;
  });

  // Auto-expand parent section when navigating to a child page
  useEffect(() => {
    // /club-admin/reports/members is surfaced under Members, not Reports
    if (location.pathname === '/club-admin/reports/members') {
      if (!expandedSections.has('/club-admin/members')) {
        setExpandedSections(prev => new Set(prev).add('/club-admin/members'));
      }
      return;
    }

    const pathToParent: Record<string, string> = {
      '/club-admin/tee-sheet': '/club-admin/tee-sheet',
      '/club-admin/member': '/club-admin/members',
      '/club-admin/staff': '/club-admin/staff',
      '/club-admin/coaching': '/club-admin/coaching',
      '/club-admin/services': '/club-admin/services',
      '/club-admin/revenue': '/club-admin/revenue',
      '/club-admin/reports': '/club-admin/reports',
    };

    for (const [pathPrefix, parentPath] of Object.entries(pathToParent)) {
      if (location.pathname.startsWith(pathPrefix) && !expandedSections.has(parentPath)) {
        setExpandedSections(prev => new Set(prev).add(parentPath));
        break;
      }
    }
  }, [location.pathname]);

  const {
    currentClub,
    adminClubs,
    hasMultipleClubs,
    switchClub,
    isLoading,
    canAccessAdmin,
    clubDetails
  } = useClubAdmin();

  // Fetch feature access for current club
  const { data: featureAccess, isLoading: featureAccessLoading } = useClubFeatureAccess();

  // Fetch staff permissions for the current user (stored in staff_notes)
  const { canAccessRoute, isLoading: permissionsLoading } = useStaffPermissions();

  // Filter sidebar items based on feature access AND staff permissions
  const allSidebarItems = getSidebarItems(t);
  const userRole = currentClub?.admin_role;

  const sidebarItems = allSidebarItems.filter((item) => {
    // Permission-based filtering for non-owner/admin roles
    // Uses the granular permissions stored in staff_notes (set via the permission editor)
    if (userRole !== 'owner' && userRole !== 'admin') {
      // Check if user has permission to access this route
      if (!canAccessRoute(item.path)) return false;
    }

    // Feature-based filtering (club-level feature toggles)
    const featureKey = PATH_TO_FEATURE_KEY[item.path];
    // If no feature key mapping exists, show the item by default
    if (!featureKey) return true;
    // If feature access hasn't loaded yet, show all items
    if (featureAccessLoading) return true;
    // Otherwise, check if feature is enabled
    return featureAccess?.[featureKey] ?? false;
  }).map(item => {
    // Also filter children based on permissions
    if (userRole !== 'owner' && userRole !== 'admin' && item.children) {
      return {
        ...item,
        children: item.children.filter(child => canAccessRoute(child.path))
      };
    }
    return item;
  });

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('clubAdminSidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleLogout = async () => {
    await signOut();
    navigate('/auth');
  };

  const toggleSection = (path: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const isChildActive = (item: SidebarItem) => {
    if (!item.children) return false;
    return item.children.some(child => location.pathname === child.path);
  };

  return (
    <ClubAdminLayoutContext.Provider value={true}>
      <CustomHeaderActionsContext.Provider value={{ setCustomHeaderActions: setNestedHeaderActions }}>
      <WeekSelectorContext.Provider value={weekSelectorContextValue}>
      <CrmPageTracker />
      <div className="flex h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Mobile sidebar overlay */}
        {sidebarOpen ? <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
                       /> : null}

        {/* Sidebar */}
        <TooltipProvider>
        <div className={cn(
        "shadow-xl border-r border-slate-200/60 z-50",
        "fixed inset-y-0 left-0 transform md:relative md:translate-x-0 transition-all duration-300 ease-in-out",
        sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        "flex flex-col h-full rounded-tr-3xl rounded-br-3xl",
        isEffectivelyCollapsed ? "w-16" : "w-72",
        "bg-white md:bg-white/80 md:backdrop-blur-sm"
      )}
        >
          {/* Collapse Toggle Button - Desktop Only */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden md:flex absolute -right-3 top-20 z-10 w-6 h-6 bg-white rounded-full border border-slate-200 shadow-lg items-center justify-center hover:bg-slate-50 transition-colors tap-target shrink-0"
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? (
              <PanelRightClose className="h-4 w-4 text-slate-600" />
            ) : (
              <PanelLeftClose className="h-4 w-4 text-slate-600" />
            )}
          </button>

          {/* Club Header */}
          <div
            className={cn(
              "border-b border-slate-200/60 flex-shrink-0 transition-all duration-300",
              isEffectivelyCollapsed ? "p-4" : "p-6"
            )}
            style={{ paddingTop: `calc(${isEffectivelyCollapsed ? '1rem' : '1.5rem'} + env(safe-area-inset-top, 0px))` }}
          >
            {isLoading ? (
              <div className={cn(
                "flex items-center transition-all duration-300",
                isEffectivelyCollapsed ? "justify-center" : "space-x-3"
              )}>
                <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
                {!isEffectivelyCollapsed && (
                  <div className="space-y-2">
                    <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
                  </div>
                )}
              </div>
          ) : currentClub ? (
            <div className={cn("transition-all duration-300", isEffectivelyCollapsed ? "" : "space-y-3")}>
              {isEffectivelyCollapsed ? (
                <div className="flex justify-center">
                  {clubDetails?.logo_url ? (
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg p-1">
                      <img
                        src={clubDetails.logo_url}
                        alt={currentClub.name}
                        className="w-full h-full object-contain rounded-lg"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' }}>
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex items-center space-x-3">
                    {clubDetails?.logo_url ? (
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg p-1">
                        <img
                          src={clubDetails.logo_url}
                          alt={currentClub.name}
                          className="w-full h-full object-contain rounded-lg"
                        />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' }}>
                        <Building2 className="h-5 w-5 text-white" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h2 className="font-bold text-slate-900 truncate">{currentClub.name}</h2>
                    </div>
                  </div>
                  
                  {/* Club Switcher for Multi-Club Admins */}
                  {hasMultipleClubs ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full justify-between bg-white/80 border-slate-200 shadow-sm hover:bg-white transition-all duration-200">
                          <span className="truncate">{currentClub.name}</span>
                          <ChevronDown className="h-4 w-4 ml-2 flex-shrink-0" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-56">
                        {adminClubs.map((club) => (
                          <DropdownMenuItem 
                            key={club.id}
                            onClick={() => switchClub(club)}
                            className={cn(
                                "flex items-center space-x-2",
                                currentClub.id === club.id && "bg-green-50 text-green-700"
                              )}
                          >
                            <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                              <Building2 className="h-3 w-3 text-white" />
                            </div>
                            <span className="truncate">{club.name}</span>
                          </DropdownMenuItem>
                          ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </>
              )}
            </div>
          ) : (
            <div className={cn(
              "flex items-center transition-all duration-300",
              isEffectivelyCollapsed ? "justify-center" : "space-x-3"
            )}>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-red-600" />
              </div>
              {!isEffectivelyCollapsed && (
                <div>
                  <h2 className="font-semibold text-gray-900">{t.clubAdmin.status.noClubSelected}</h2>
                  <p className="text-sm text-red-500">{t.clubAdmin.status.unableToLoadClub}</p>
                </div>
              )}
            </div>
          )}
          </div>

          {/* Navigation */}
          <nav className="p-4 flex-1 overflow-y-auto overflow-x-hidden">
            <ul className="space-y-2">
              {sidebarItems.map((item) => {
              const Icon = item.icon;
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = expandedSections.has(item.path);
              const isActive = hasChildren ? isChildActive(item) : location.pathname === item.path;
              const isParentActive = hasChildren && isChildActive(item);

              const buttonContent = (
                <Button
                  variant="ghost"
                  style={(isActive || isParentActive) ? { background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' } : {}}
                  className={cn(
                    "w-full rounded-xl transition-all duration-200 h-12",
                    isEffectivelyCollapsed ? "justify-center px-2" : "justify-start px-4",
                    (isActive || isParentActive)
                      ? "text-white font-semibold shadow-lg hover:shadow-xl"
                      : "bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900 hover:shadow-md font-medium"
                  )}
                  onClick={() => {
                    if (hasChildren) {
                      if (isEffectivelyCollapsed) {
                        setSidebarCollapsed(false);
                        setTimeout(() => toggleSection(item.path), 100);
                      } else {
                        toggleSection(item.path);
                      }
                    } else {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }
                  }}
                >
                  <Icon className={cn("h-5 w-5", !isEffectivelyCollapsed && "mr-3")} />
                  {!isEffectivelyCollapsed && (
                    <>
                      <span className="flex-1 text-left">{item.label}</span>
                      {hasChildren && (
                        isExpanded
                          ? <ChevronUp className="h-4 w-4 ml-2" />
                          : <ChevronDown className="h-4 w-4 ml-2" />
                      )}
                    </>
                  )}
                </Button>
              );

              return (
                <li key={item.path}>
                  {/* Parent Item */}
                  {isEffectivelyCollapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {buttonContent}
                      </TooltipTrigger>
                      <TooltipContent side="right" className="ml-2">
                        {item.label}
                        {hasChildren && isExpanded && item.children && (
                          <div className="mt-1 pt-1 border-t border-slate-200">
                            {item.children.map((child) => (
                              <div key={child.path} className="text-xs py-0.5">
                                {child.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    buttonContent
                  )}

                  {/* Child Items */}
                  {hasChildren && isExpanded && !isEffectivelyCollapsed && (
                    <ul className="mt-2 ml-4 space-y-1">
                      {item.children!.map((child) => {
                        const ChildIcon = child.icon;
                        const isChildActive = location.pathname === child.path;

                        return (
                          <li key={child.path}>
                            <Button
                              variant="ghost"
                              className={cn(
                                "w-full justify-start h-10 px-4 rounded-lg transition-all duration-200 text-sm",
                                isChildActive
                                  ? "bg-blue-50 text-blue-700 font-semibold hover:!bg-blue-100 hover:!text-blue-800"
                                  : "text-slate-600 hover:!bg-slate-50 hover:!text-slate-800"
                              )}
                              onClick={() => {
                                navigate(child.path);
                                setSidebarOpen(false);
                              }}
                            >
                              <ChildIcon className="mr-3 h-4 w-4" />
                              {child.label}
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
            </ul>
          </nav>

          {/* User Info & Logout */}
          <div className={cn(
            "border-t border-slate-200/60 flex-shrink-0 transition-all duration-300",
            isEffectivelyCollapsed ? "p-2" : "p-4"
          )}>
            {isEffectivelyCollapsed ? (
              <div className="flex flex-col items-center space-y-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shadow-sm cursor-pointer overflow-hidden relative">
                      {profile?.avatar_url && !avatarError ? (
                        <img
                          src={profile.avatar_url}
                          alt={profile.display_name || user?.email || 'User'}
                          className="w-full h-full object-cover"
                          onError={() => setAvatarError(true)}
                        />
                      ) : null}
                      <div
                        className={cn(
                          "w-full h-full bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center",
                          profile?.avatar_url && !avatarError ? "absolute inset-0 opacity-0" : "static"
                        )}
                      >
                        <span className="text-white text-sm font-bold">
                          {profile?.display_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="ml-2">
                    <div>
                      <p className="font-semibold">{profile?.display_name || user?.email?.split('@')[0] || 'User'}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
                {/* Language Selector - Collapsed */}
                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-500 hover:text-slate-700 hover:bg-white/80 rounded-lg w-8 h-8 p-0"
                        >
                          <span className="text-base">{languageOptions.find(l => l.code === language)?.flag || '🌐'}</span>
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="ml-2">
                      {t.clubAdmin?.layout?.language ?? 'Language'}
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent align="end" side="right" className="ml-2">
                    {languageOptions.map((lang) => (
                      <DropdownMenuItem
                        key={lang.code}
                        onClick={() => setLanguage(lang.code)}
                        className={cn(
                          "flex items-center space-x-2 cursor-pointer",
                          language === lang.code && "bg-blue-50 text-blue-700"
                        )}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.label}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleLogout}
                      className="text-slate-500 hover:text-slate-700 hover:bg-white/80 rounded-lg w-8 h-8 p-0"
                    >
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="ml-2">
                    {t.clubAdmin?.layout?.logout ?? 'Logout'}
                  </TooltipContent>
                </Tooltip>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Language Selector Row */}
                <div className="flex items-center justify-between px-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-500 hover:text-slate-700 hover:bg-white/80 rounded-lg px-2 h-8"
                      >
                        <Globe className="h-4 w-4 mr-1.5" />
                        <span className="text-xs">{languageOptions.find(l => l.code === language)?.label || 'English'}</span>
                        <ChevronDown className="h-3 w-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top" className="w-36">
                      {languageOptions.map((lang) => (
                        <DropdownMenuItem
                          key={lang.code}
                          onClick={() => setLanguage(lang.code)}
                          className={cn(
                            "flex items-center space-x-2 cursor-pointer",
                            language === lang.code && "bg-blue-50 text-blue-700"
                          )}
                        >
                          <span>{lang.flag}</span>
                          <span>{lang.label}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {/* User Info Row */}
                <div className="flex items-center justify-between bg-white/50 backdrop-blur-sm rounded-xl p-3">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center shadow-sm overflow-hidden relative">
                      {profile?.avatar_url && !avatarError ? (
                        <img
                          src={profile.avatar_url}
                          alt={profile.display_name || user?.email || 'User'}
                          className="w-full h-full object-cover"
                          onError={() => setAvatarError(true)}
                        />
                      ) : null}
                      <div
                        className={cn(
                          "w-full h-full bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center",
                          profile?.avatar_url && !avatarError ? "absolute inset-0 opacity-0" : "static"
                        )}
                      >
                        <span className="text-white text-sm font-bold">
                          {profile?.display_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{profile?.display_name || user?.email?.split('@')[0] || 'User'}</p>
                      <p className="text-xs text-slate-600 truncate">{user?.email}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="text-slate-500 hover:text-slate-700 hover:bg-white/80 rounded-lg flex-shrink-0"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        </TooltipProvider>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden md:ml-0">
          {/* Top Header */}
          <header className="px-6 pb-4 flex-shrink-0 safe-area-top-4" style={{ background: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)', borderBottom: '1px solid rgba(15,23,42,0.08)', boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 8px rgba(15,23,42,0.06)' }}>
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              {/* Mobile menu button and title section */}
              <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 min-w-0 flex-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="md:hidden flex-shrink-0"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
                </Button>

                <div className="p-3 rounded-xl text-white shadow-lg flex-shrink-0" style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' }}>
                  {(() => {
                  // First try to find exact match
                  let currentItem = sidebarItems.find(item => item.path === location.pathname);
                  // If not found, check children
                  if (!currentItem) {
                    for (const item of sidebarItems) {
                      if (item.children) {
                        const childItem = item.children.find(child => child.path === location.pathname);
                        if (childItem) {
                          currentItem = childItem;
                          break;
                        }
                      }
                    }
                  }
                  const Icon = currentItem?.icon || LayoutDashboard;
                  return <Icon className="w-5 h-5" />;
                })()}
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-[22px] font-bold tracking-tight text-slate-900 leading-tight truncate">
                    {(() => {
                    const currentPath = location.pathname;
                    if (currentPath === '/club-admin/members') return t.clubAdmin.headers.memberManagement;
                    if (currentPath === '/club-admin/staff') return t.clubAdmin.headers.staffSchedule;
                    if (currentPath === '/club-admin/staff/people') return t.clubAdmin.headers.peopleManagement;
                    if (currentPath === '/club-admin/bookings') return t.clubAdmin.headers.bookingManagement;
                    if (currentPath === '/club-admin/tee-sheet') return t.clubAdmin.headers.teeSheet;
                    if (currentPath === '/club-admin/tee-sheet/check-in') return t.clubAdmin.headers.checkInStation;
                    if (currentPath === '/club-admin/tee-sheet/generate') return t.clubAdmin.headers.teeSheetCalendar;
                    if (currentPath === '/club-admin/tee-sheet/analytics') return t.clubAdmin.headers.teeSheetAnalytics;
                    if (currentPath === '/club-admin/tee-sheet/settings') return t.clubAdmin.headers.teeSheetSettings;
                    if (currentPath === '/club-admin/courses') return t.clubAdmin.headers.courseManagement;
                    if (currentPath === '/club-admin/services') return t.clubAdmin.headers.rangeManagement;
                    if (currentPath === '/club-admin/services/carts') return t.clubAdmin.headers.cartManagement;
                    if (currentPath === '/club-admin/services/lockers') return t.clubAdmin.headers.lockerManagement;
                    if (currentPath === '/club-admin/services/config') return t.clubAdmin.headers.servicesConfig;
                    if (currentPath === '/club-admin/members/memberships') return t.clubAdmin.headers.membershipManagement;
                    if (currentPath === '/club-admin/revenue') return t.clubAdmin?.headers?.revenueDashboard ?? 'Revenue Dashboard';
                    if (currentPath === '/club-admin/revenue/transactions') return t.clubAdmin?.navigation?.transactions ?? 'Transactions';
                    if (currentPath === '/club-admin/revenue/settings') return t.clubAdmin?.navigation?.paymentSettings ?? 'Payment Settings';
                    if (currentPath === '/club-admin/communication') return t.clubAdmin.headers.communication;
                    if (currentPath === '/club-admin/announcements' || currentPath.startsWith('/club-admin/announcements/')) return t.clubAdmin.headers.announcementManagement;
                    if (currentPath === '/club-admin/events' || currentPath.startsWith('/club-admin/events/')) return t.clubAdmin.headers.eventsManagement;
                    if (currentPath === '/club-admin/coaching') return t.clubAdmin.headers.coachingPortal;
                    if (currentPath === '/club-admin/coaching/settings') return t.clubAdmin.headers.coachingSettings;
                    if (currentPath === '/club-admin/member-progress' || currentPath.startsWith('/club-admin/member-progress/')) return (t.admin as { memberProgress?: { title?: string } })?.memberProgress?.title ?? 'Member Progress';
                    if (currentPath === '/club-admin/analytics') return t.clubAdmin.headers.analyticsHeader;
                    if (currentPath === '/club-admin/settings/coaches') return t.clubAdmin.headers.coachRoster;
                    if (currentPath === '/club-admin/settings') return t.clubAdmin.headers.clubSettings;
                    if (currentPath === '/club-admin/feedback') return t.clubAdmin.headers.giveFeedback;
                    if (currentPath === '/club-admin/dashboard') return t.clubAdmin?.headers?.reportsOverview ?? 'Club Dashboard';
                    if (currentPath === '/club-admin/reports') return t.clubAdmin?.headers?.reportsOverview ?? 'Reports Overview';
                    if (currentPath === '/club-admin/reports/members') return t.clubAdmin?.headers?.memberEngagement ?? 'Member Engagement';
                    if (currentPath === '/club-admin/reports/revenue') return t.clubAdmin?.headers?.revenueReports ?? 'Revenue Reports';
                    if (currentPath === '/club-admin/reports/tee-sheet') return t.clubAdmin?.headers?.teeSheetReports ?? 'Tee Sheet Reports';
                    if (currentPath === '/club-admin/reports/communications') return t.clubAdmin?.headers?.communicationsReports ?? 'Communications Reports';
                    if (currentPath === '/club-admin/reports/services') return t.clubAdmin?.headers?.servicesReports ?? 'Services Reports';
                    if (currentPath === '/club-admin/reports/events') return t.clubAdmin?.headers?.eventsReports ?? 'Events Reports';
                    return sidebarItems.find(item => item.path === currentPath)?.label || t.clubAdmin.navigation.dashboard;
                  })()}
                  </h1>
                  <p className="text-sm text-slate-500 font-medium mt-0.5 hidden sm:block">
                    {(() => {
                    const currentPath = location.pathname;
                    if (currentPath === '/club-admin/members') return t.clubAdmin.descriptions.memberManagement;
                    if (currentPath === '/club-admin/staff') return t.clubAdmin.descriptions.staffSchedule;
                    if (currentPath === '/club-admin/staff/people') return t.clubAdmin.descriptions.peopleManagement;
                    if (currentPath === '/club-admin/bookings') return t.clubAdmin.descriptions.bookingManagement;
                    if (currentPath === '/club-admin/tee-sheet') return t.clubAdmin.descriptions.teeSheet;
                    if (currentPath === '/club-admin/tee-sheet/check-in') return t.clubAdmin.descriptions.checkInStation;
                    if (currentPath === '/club-admin/tee-sheet/generate') return t.clubAdmin.descriptions.teeSheetCalendar;
                    if (currentPath === '/club-admin/tee-sheet/analytics') return t.clubAdmin.descriptions.teeSheetAnalytics;
                    if (currentPath === '/club-admin/tee-sheet/settings') return t.clubAdmin.descriptions.teeSheetSettings;
                    if (currentPath === '/club-admin/courses') return t.clubAdmin.descriptions.courseManagement;
                    if (currentPath === '/club-admin/services') return t.clubAdmin.descriptions.rangeManagement;
                    if (currentPath === '/club-admin/services/carts') return t.clubAdmin.descriptions.cartManagement;
                    if (currentPath === '/club-admin/services/lockers') return t.clubAdmin.descriptions.lockerManagement;
                    if (currentPath === '/club-admin/services/config') return t.clubAdmin.descriptions.servicesConfig;
                    if (currentPath === '/club-admin/members/memberships') return t.clubAdmin.descriptions.membershipManagement;
                    if (currentPath === '/club-admin/revenue') return t.clubAdmin?.descriptions?.revenueDashboard ?? 'Track revenue, transactions, and financial performance';
                    if (currentPath === '/club-admin/revenue/transactions') return t.clubAdmin?.descriptions?.transactions ?? 'View and manage all payment transactions';
                    if (currentPath === '/club-admin/revenue/settings') return t.clubAdmin?.descriptions?.paymentSettings ?? 'Configure payment methods and billing settings';
                    if (currentPath === '/club-admin/communication') return t.clubAdmin.descriptions.communication;
                    if (currentPath === '/club-admin/announcements' || currentPath.startsWith('/club-admin/announcements/')) return t.clubAdmin.descriptions.announcementManagement;
                    if (currentPath === '/club-admin/events' || currentPath.startsWith('/club-admin/events/')) return t.clubAdmin.descriptions.eventsManagement;
                    if (currentPath === '/club-admin/coaching') return t.clubAdmin.descriptions.coachingPortal;
                    if (currentPath === '/club-admin/coaching/settings') return t.clubAdmin.descriptions.coachingSettings;
                    if (currentPath === '/club-admin/member-progress' || currentPath.startsWith('/club-admin/member-progress/')) return (t.admin as { memberProgress?: { subtitle?: string } })?.memberProgress?.subtitle ?? 'Track quest completion and engagement for all club members';
                    if (currentPath === '/club-admin/analytics') return t.clubAdmin.descriptions.analyticsDescription;
                    if (currentPath === '/club-admin/settings/coaches') return t.clubAdmin.descriptions.coachRoster;
                    if (currentPath === '/club-admin/settings') return t.clubAdmin.descriptions.clubSettings;
                    if (currentPath === '/club-admin/feedback') return t.clubAdmin.descriptions.giveFeedback;
                    if (currentPath === '/club-admin/dashboard') return t.clubAdmin?.descriptions?.reportsOverview ?? 'Club-wide analytics and reporting dashboard';
                    if (currentPath === '/club-admin/reports') return t.clubAdmin?.descriptions?.reportsOverview ?? 'Club-wide analytics and reporting dashboard';
                    if (currentPath === '/club-admin/reports/members') return t.clubAdmin?.descriptions?.memberEngagement ?? 'Member engagement, retention, and activity metrics';
                    if (currentPath === '/club-admin/reports/revenue') return t.clubAdmin?.descriptions?.revenueReports ?? 'Revenue analytics and financial performance';
                    if (currentPath === '/club-admin/reports/tee-sheet') return t.clubAdmin?.descriptions?.teeSheetReports ?? 'Booking patterns, utilization, and performance';
                    if (currentPath === '/club-admin/reports/communications') return t.clubAdmin?.descriptions?.communicationsReports ?? 'Announcement performance and engagement';
                    if (currentPath === '/club-admin/reports/services') return t.clubAdmin?.descriptions?.servicesReports ?? 'Range, carts, and locker analytics';
                    if (currentPath === '/club-admin/reports/events') return t.clubAdmin?.descriptions?.eventsReports ?? 'Event performance, participation, and revenue analytics';
                    return currentClub ? t.clubAdmin.headers.managingClub.replace('{clubName}', currentClub.name) : t.clubAdmin.headers.defaultManagement;
                  })()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 md:gap-4 flex-wrap sm:flex-nowrap sm:justify-end">
                {/* Week Selector for Dashboard and Reports page */}
                {(location.pathname === '/club-admin/dashboard' || location.pathname === '/club-admin/reports') && (
                  <Select value={selectedWeek} onValueChange={setSelectedWeek}>
                    <SelectTrigger className="w-9 sm:w-auto sm:min-w-[180px] h-9 border-slate-200 bg-white/80 backdrop-blur-sm text-sm gap-2 [&>svg:last-child]:hidden sm:[&>svg:last-child]:block justify-center sm:justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <span className="hidden sm:inline font-medium">{currentWeekOption?.label}</span>
                        <span className="hidden sm:inline text-slate-400">·</span>
                        <span className="hidden sm:inline text-slate-500">{currentWeekOption?.dateRange}</span>
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {weekOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{option.label}</span>
                            <span className="text-slate-400">·</span>
                            <span className="text-slate-500 text-xs">{option.dateRange}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {/* Period Selector for Coaching Revenue page */}
                {location.pathname === '/club-admin/coaching/revenue' && (
                  <Select value={selectedCoachingPeriod} onValueChange={setSelectedCoachingPeriod}>
                    <SelectTrigger className="w-9 sm:w-auto sm:min-w-[140px] h-9 border-slate-200 bg-white/80 backdrop-blur-sm text-sm gap-2 [&>svg:last-child]:hidden sm:[&>svg:last-child]:block justify-center sm:justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <span className="hidden sm:inline font-medium">{currentCoachingPeriod?.label}</span>
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {coachingPeriodOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          <span className="font-medium">{option.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {location.pathname === '/club-admin/courses' && onAddCourse ? <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button 
                        onClick={onAddCourse} 
                        className="p-2 sm:px-3 sm:py-2 rounded-lg shadow-lg flex items-center justify-center transition-all duration-200 text-white font-medium tap-target shrink-0"
                        style={{
                          background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)'
                        }}
                      >
                        <svg className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span className="hidden md:inline ml-1.5 sm:ml-2 whitespace-nowrap">{t.clubAdmin.actions.addCourse}</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="md:hidden">
                      <p>{t.clubAdmin.actions.addCourse}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider> : null}
                {location.pathname === '/club-admin/services/carts' && onAddCart && onNewCartBooking ? <div className="flex gap-1.5 sm:gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button 
                          onClick={onNewCartBooking} 
                          className="p-2 sm:px-3 sm:py-2 rounded-lg shadow-sm flex items-center justify-center transition-all duration-200 text-slate-700 font-medium bg-white/80 backdrop-blur-sm border border-slate-200 hover:bg-slate-50 hover:border-slate-300 tap-target shrink-0"
                        >
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0a2 2 0 012-2h4a2 2 0 012 2v10a2 2 0 01-2 2H10a2 2 0 01-2-2V7z" />
                          </svg>
                          <span className="hidden md:inline ml-1.5 sm:ml-2 whitespace-nowrap">{t.clubAdmin.actions.newCartBooking}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="md:hidden">
                        <p>{t.clubAdmin.actions.newCartBooking}</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button 
                          onClick={onAddCart} 
                          className="p-2 sm:px-3 sm:py-2 rounded-lg shadow-lg flex items-center justify-center transition-all duration-200 text-white font-medium tap-target shrink-0"
                          style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' }}
                        >
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <span className="hidden md:inline ml-1.5 sm:ml-2 whitespace-nowrap">{t.clubAdmin.actions.addCart}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="md:hidden">
                        <p>{t.clubAdmin.actions.addCart}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div> : null}
                {location.pathname === '/club-admin/channels-legacy' && onCreateChannel ? <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button 
                        onClick={onCreateChannel} 
                        className="p-2 sm:px-3 sm:py-2 rounded-lg shadow-lg flex items-center justify-center transition-all duration-200 text-white font-medium tap-target shrink-0"
                        style={{
                          background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)'
                        }}
                      >
                        <svg className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span className="hidden md:inline ml-1.5 sm:ml-2 whitespace-nowrap">{t.clubAdmin.actions.createChannel}</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="md:hidden">
                      <p>{t.clubAdmin.actions.createChannel}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider> : null}
                {location.pathname === '/club-admin/members' && onInviteMember && onExportMembers ? <div className="flex gap-1.5 sm:gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button 
                          onClick={onExportMembers} 
                          className="p-2 sm:px-3 sm:py-2 rounded-lg shadow-lg flex items-center justify-center transition-all duration-200 text-slate-600 font-medium bg-white/80 backdrop-blur-sm border border-blue-200 hover:bg-white/90 tap-target shrink-0"
                        >
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="hidden md:inline ml-1.5 sm:ml-2 whitespace-nowrap">{t.clubAdmin.actions.export}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="md:hidden">
                        <p>{t.clubAdmin.actions.export}</p>
                      </TooltipContent>
                    </Tooltip>
                    {onBulkImport ? <Tooltip>
                      <TooltipTrigger asChild>
                        <button 
                          onClick={onBulkImport} 
                          className="p-2 sm:px-3 sm:py-2 rounded-lg shadow-lg flex items-center justify-center transition-all duration-200 text-slate-600 font-medium bg-white/80 backdrop-blur-sm border border-green-200 hover:bg-white/90 tap-target shrink-0"
                        >
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                          </svg>
                          <span className="hidden md:inline ml-1.5 sm:ml-2 whitespace-nowrap">{t.clubAdmin?.layout?.bulkImport ?? 'Bulk Import'}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="md:hidden">
                        <p>{t.clubAdmin?.layout?.bulkImport ?? 'Bulk Import'}</p>
                      </TooltipContent>
                    </Tooltip> : null}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button 
                          onClick={onInviteMember} 
                          className="p-2 sm:px-3 sm:py-2 rounded-lg shadow-lg flex items-center justify-center transition-all duration-200 text-white font-medium tap-target shrink-0"
                          style={{
                            background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)'
                          }}
                        >
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                          </svg>
                          <span className="hidden md:inline ml-1.5 sm:ml-2 whitespace-nowrap">{t.clubAdmin.actions.inviteMember}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="md:hidden">
                        <p>{t.clubAdmin.actions.inviteMember}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                                                                                                    </div> : null}
                {location.pathname === '/club-admin/analytics' && onDateRangeChange && onFilters ? <div className="flex gap-1.5 sm:gap-2">
                  <select
                    onChange={(e) => onDateRangeChange(e.target.value)}
                    className="px-2 sm:px-3 md:px-4 py-2 rounded-lg shadow-lg text-xs sm:text-sm text-slate-600 font-medium bg-white/80 backdrop-blur-sm border border-slate-200 hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[2.25rem] sm:max-w-none"
                    defaultValue="30d"
                  >
                    <option value="7d">{t.clubAdmin.dateRanges.last7Days}</option>
                    <option value="30d">{t.clubAdmin.dateRanges.last30Days}</option>
                    <option value="3m">{t.clubAdmin.dateRanges.last3Months}</option>
                    <option value="1y">{t.clubAdmin.dateRanges.lastYear}</option>
                  </select>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button 
                          onClick={onFilters} 
                          className="p-2 sm:px-3 sm:py-2 rounded-lg shadow-lg flex items-center justify-center transition-all duration-200 text-slate-600 font-medium bg-white/80 backdrop-blur-sm border border-slate-200 hover:bg-white/90 tap-target shrink-0"
                        >
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                          </svg>
                          <span className="hidden md:inline ml-1.5 sm:ml-2 whitespace-nowrap">{t.clubAdmin.actions.filters}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="md:hidden">
                        <p>{t.clubAdmin.actions.filters}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                                                                                                   </div> : null}
                {location.pathname === '/club-admin/staff' && onCreateTask && onScheduleShift ? <div className="flex gap-1.5 sm:gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={onScheduleShift}
                          className="p-2 sm:px-3 sm:py-2 rounded-lg shadow-lg flex items-center justify-center transition-all duration-200 text-slate-600 font-medium bg-white/80 backdrop-blur-sm border border-blue-200 hover:bg-white/90 tap-target shrink-0"
                        >
                          <Calendar className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                          <span className="hidden md:inline ml-1.5 sm:ml-2 whitespace-nowrap">{t.clubAdmin.actions.scheduleShift}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="md:hidden">
                        <p>{t.clubAdmin.actions.scheduleShift}</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={onCreateTask}
                          className="p-2 sm:px-3 sm:py-2 rounded-lg shadow-lg flex items-center justify-center transition-all duration-200 text-white font-medium tap-target shrink-0"
                          style={{
                            background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)'
                          }}
                        >
                          <Plus className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                          <span className="hidden md:inline ml-1.5 sm:ml-2 whitespace-nowrap">{t.clubAdmin.actions.createTask}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="md:hidden">
                        <p>{t.clubAdmin.actions.createTask}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                                                                                                </div> : null}
                {location.pathname === '/club-admin/settings' && onSaveSettings ? <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={onSaveSettings}
                        disabled={isSaving}
                        className={cn(
                          "p-2 sm:px-3 sm:py-2 rounded-lg shadow-lg flex items-center justify-center transition-all duration-200 text-white font-medium tap-target shrink-0",
                          isSaving && "opacity-50 cursor-not-allowed"
                        )}
                        style={{
                          background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)'
                        }}
                      >
                        {isSaving ? (
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 animate-spin flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                        )}
                        <span className="hidden md:inline ml-1.5 sm:ml-2 whitespace-nowrap">{isSaving ? t.clubAdmin.actions.saving : t.clubAdmin.actions.saveChanges}</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="md:hidden">
                      <p>{isSaving ? t.clubAdmin.actions.saving : t.clubAdmin.actions.saveChanges}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider> : null}
                {/* Custom header actions from child pages (via props or context) */}
                {customHeaderActions}
                {nestedHeaderActions}
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-auto p-4 sm:p-6">
            <MemberDetailPanelProvider clubId={currentClub?.id}>
              {children ?? (
                <Suspense fallback={<ContentSkeleton />}>
                  <Outlet />
                </Suspense>
              )}
            </MemberDetailPanelProvider>
          </main>
        </div>
      </div>
      </WeekSelectorContext.Provider>
      </CustomHeaderActionsContext.Provider>
    </ClubAdminLayoutContext.Provider>
  );
}